import type { BufferGeometry, Texture, Vector2 } from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import type { LineMaterial, LineMaterialParameters } from 'three/examples/jsm/lines/LineMaterial.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import type { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import type { CadClipBinding } from '@/plugins/cad/module/CADModule/object/material/CadClipBinding'

/**
 * CAD 领域内的双后端宽线工厂（阶段 5.11.1）。
 *
 * 只负责「按 backend 选择实现类 + 统一分辨率/虚线缩放语义」，不改变任何现有 WebGL 行为：
 * - WebGL2 继续使用 `MaterialUtils.createMaterialByLineType()` 产出的 `LineMaterial` 及其
 *   `DashDotLineMaterial` / `DoubleDashLineMaterial` 子类（这些子类靠 GLSL `onBeforeCompile`
 *   实现点划线/双划线图案，属 5.12 的 TSL 迁移范围）。
 * - WebGPU 使用 `three/addons/lines/webgpu/Line2` 配 `Line2NodeMaterial`；node 材质的
 *   dash/dot/double-dash 图案由 5.12 的 TSL 组合实现，本层先保证线宽、颜色、基础虚线与
 *   dash scale 语义对齐。
 *
 * 与工具侧的 `tools/shared/wideLine` 分开：CAD 的材质是长生命周期、被 `MaterialManager`
 * 缓存与 `MaterialUtils` 统一更新 dash scale 的，语义与工具一次性预览线不同，因此不共用工厂。
 */

/** WebGPU node 宽线材质的最小子集（避免静态依赖 three/webgpu）。 */
export interface CadNodeLineMaterial extends LineMaterial {
  /** `Line2NodeMaterial` 用 `scale` 表达 WebGL `dashScale`。 */
  scale: number
  /** TSL 材质在构建阶段可覆盖的私有入口。 */
  setupPosition: (builder: unknown) => unknown
  /** TSL 材质在构建阶段可覆盖的私有入口。 */
  setupDiffuseColor: (builder: unknown) => void
  /** TSL 图案节点覆盖；null 时使用 Three 原生 dashSize。 */
  dashSizeNode: unknown | null
  /** TSL 图案节点覆盖；null 时使用 Three 原生 gapSize。 */
  gapSizeNode: unknown | null
}

/** static batch 的 WebGPU dash scale uniform 节点。 */
export interface CadBatchDashScaleUniforms {
  readonly perspective: { value: number }
  readonly unitScale: { value: number }
  readonly near: { value: number }
}

/** WebGPU 线型图案。`regular` 保留 Three 原生 dash，另外两类由 CAD TSL 扩展。 */
export type CadLinePattern = 'regular' | 'dashDot' | 'doubleDash'

type CadTslModule = typeof import('three/tsl')

/** WebGPU 宽线模块（惰性加载，避免 WebGL 宿主拖入 WebGPU 构建）。 */
export interface CadWebGpuLineModule {
  readonly Line2NodeMaterial: new (parameters: Record<string, unknown>) => CadNodeLineMaterial
  readonly Line2: new (geometry: BufferGeometry, material: CadNodeLineMaterial) => Line2
  readonly LineSegments2: new (
    geometry: BufferGeometry,
    material: CadNodeLineMaterial
  ) => LineSegments2
  readonly tsl: CadTslModule
}

let webgpuModulePromise: Promise<CadWebGpuLineModule> | null = null

/** 惰性加载 `Line2NodeMaterial`；只在 CAD 真正跑在 WebGPU 时加载。 */
export function loadCadWebGpuLineModule(): Promise<CadWebGpuLineModule> {
  webgpuModulePromise ??= Promise.all([
    import('three/webgpu'),
    import('three/examples/jsm/lines/webgpu/Line2.js'),
    import('three/examples/jsm/lines/webgpu/LineSegments2.js'),
    import('three/tsl'),
  ]).then(([webgpuModule, line2Module, lineSegments2Module, tsl]) => ({
    Line2NodeMaterial:
      webgpuModule.Line2NodeMaterial as unknown as CadWebGpuLineModule['Line2NodeMaterial'],
    Line2: line2Module.Line2 as unknown as CadWebGpuLineModule['Line2'],
    LineSegments2:
      lineSegments2Module.LineSegments2 as unknown as CadWebGpuLineModule['LineSegments2'],
    tsl,
  }))
  return webgpuModulePromise
}

/** CAD 线材质参数：与 `LineMaterialParameters` 同形，额外允许 node 材质忽略的字段。 */
export type CadLineMaterialParameters = LineMaterialParameters

/**
 * 已注册的 WebGPU 宽线模块（同步槽位）。
 *
 * `three/webgpu` 必须惰性加载，而 CAD 的线创建点是同步的，因此由 `CadPlugin.preload()`
 * 在 WebGPU 下预加载并注册，之后同步创建 node 材质。
 */
let registeredWebGpuLineModule: CadWebGpuLineModule | null = null

/** 当前 CAD 线创建使用的 backend；由 CadPlugin.preload() 按引擎上下文设置。 */
let currentBackend: GraphicsBackendKind = 'webgl2'
let currentClipBinding: CadClipBinding | null = null

/** 设置当前 CAD 线创建 backend（WebGL2 默认，WebGPU 由 preload 设置）。 */
export function setCadLineBackend(backend: GraphicsBackendKind): void {
  currentBackend = backend
}

/** 读取当前 CAD 线创建 backend。 */
export function getCadLineBackend(): GraphicsBackendKind {
  return currentBackend
}

/** 读取已注册的 WebGPU 宽线模块；未 preload 时返回 null。 */
export function getRegisteredCadWebGpuLineModule(): CadWebGpuLineModule | null {
  return registeredWebGpuLineModule
}

/** 设置当前 CAD WebGPU 裁切绑定。 */
export function setCadClipBinding(binding: CadClipBinding | null): void {
  currentClipBinding = binding
}

/** 注册已预加载的 WebGPU 宽线模块；传 null 清理（rollbackPreload/dispose）。 */
export function registerCadWebGpuLineModule(module: CadWebGpuLineModule | null): void {
  registeredWebGpuLineModule = module
}

/** 当前是否已注册 WebGPU 宽线模块。 */
export function hasCadWebGpuLineModule(): boolean {
  return registeredWebGpuLineModule !== null
}

/**
 * 同步创建 CAD 线材质（创建点使用）。
 *
 * WebGL2 复用调用方工厂；WebGPU 使用已预加载的 node 模块，**未预加载时抛出明确错误**，
 * 不允许静默回退到 GLSL 子类（那会在 WebGPU 下产生无效材质）。
 */
export function createCadLineMaterialSync(input: {
  backend: GraphicsBackendKind
  options: CadLineMaterialParameters
  createWebGlMaterial: (options: CadLineMaterialParameters) => LineMaterial
  pattern?: CadLinePattern
}): LineMaterial {
  if (!prefersCadNodeLineMaterial(input.backend)) {
    return input.createWebGlMaterial(input.options)
  }

  if (!registeredWebGpuLineModule) {
    throw new Error('CAD WebGPU 线材质需要先 preload 注册 Line2NodeMaterial（CadPlugin.preload）')
  }

  const material = new registeredWebGpuLineModule.Line2NodeMaterial(
    toCadNodeMaterialParameters(input.options)
  )
  applyCadNodeLinePattern(material, input.pattern, registeredWebGpuLineModule.tsl)
  currentClipBinding?.applyToMaterial(material)
  return material
}

/** 把 CAD 私有线型片段挂到 `Line2NodeMaterial` 的构建阶段。 */
export function applyCadNodeLinePattern(
  material: CadNodeLineMaterial,
  pattern: CadLinePattern = 'regular',
  tsl: CadTslModule,
  options: { batchDashScale?: boolean } = {}
): void {
  if (pattern === 'regular' && !options.batchDashScale) return

  const {
    attribute,
    float,
    materialLineDashOffset,
    materialLineScale,
    modelViewMatrix,
    positionGeometry,
    uniform,
    uv,
    vec4,
    varyingProperty,
  } = tsl
  const cadLineDistance = varyingProperty('float', 'cadLineDistance')
  const originalSetupPosition = material.setupPosition.bind(material)
  const originalSetupDiffuseColor = material.setupDiffuseColor.bind(material)

  const batchUniforms = options.batchDashScale
    ? {
        perspective: uniform(0),
        unitScale: uniform(1),
        near: uniform(0.1),
      }
    : null
  if (batchUniforms) material.userData.cadDashUniforms = batchUniforms
  material.userData.cadLinePattern = pattern

  // 保留 Three 的线距离/端点处理，但禁用它默认的单虚线 discard 图案。
  material.dashSizeNode = float(1e9)
  material.gapSizeNode = float(0)
  material.setupPosition = (builder) => {
    const result = originalSetupPosition(builder)
    const distanceStart = attribute('instanceDistanceStart', 'float')
    const distanceEnd = attribute('instanceDistanceEnd', 'float')
    const distance = positionGeometry.y.lessThan(0.5).select(distanceStart, distanceEnd)
    const distanceScale = batchUniforms
      ? (() => {
          const reference = attribute('instanceDashReference', 'vec3')
          const depth = modelViewMatrix.mul(vec4(reference, 1)).z.negate()
          return batchUniforms.perspective
            .greaterThan(0.5)
            .and(depth.greaterThan(batchUniforms.near))
            .select(batchUniforms.unitScale.div(depth), materialLineScale)
        })()
      : materialLineScale
    cadLineDistance.assign(distance.mul(distanceScale).add(materialLineDashOffset))
    return result
  }
  material.setupDiffuseColor = (builder) => {
    originalSetupDiffuseColor(builder)

    const dashSize = float(material.dashSize)
    const gapSize = float(material.gapSize)
    if (pattern === 'regular') {
      cadLineDistance.mod(dashSize.add(gapSize)).greaterThan(dashSize).discard()
      return
    }

    if (pattern === 'dashDot') {
      const dotSize = dashSize.mul(0.25)
      const totalSize = dashSize.add(gapSize).add(dotSize).add(gapSize)
      const modulo = cadLineDistance.mod(totalSize)
      const isDash = modulo.lessThan(dashSize)
      const isDot = modulo
        .greaterThan(dashSize.add(gapSize))
        .and(modulo.lessThan(dashSize.add(gapSize).add(dotSize)))
      isDash.or(isDot).not().discard()
      return
    }

    const totalPattern = dashSize.add(gapSize)
    const halfPattern = totalPattern.mul(0.5)
    const upperHalfOffset = uv().x.greaterThanEqual(0).select(halfPattern, float(0))
    uv().x.abs().lessThan(0.35).discard()
    cadLineDistance.add(upperHalfOffset).mod(totalPattern).greaterThan(dashSize).discard()
  }
}

/** 克隆 CAD node 线材质并重建 TSL 图案，供 static batch 使用。 */
export function cloneCadNodeLineMaterial(source: CadNodeLineMaterial): CadNodeLineMaterial {
  if (!registeredWebGpuLineModule) {
    throw new Error('CAD WebGPU 线材质需要先 preload 注册 Line2NodeMaterial（CadPlugin.preload）')
  }
  const clone = source.clone() as unknown as CadNodeLineMaterial
  const pattern = (source.userData.cadLinePattern as CadLinePattern | undefined) ?? 'regular'
  applyCadNodeLinePattern(clone, pattern, registeredWebGpuLineModule.tsl, {
    batchDashScale: true,
  })
  currentClipBinding?.applyToMaterial(clone)
  return clone
}

/** 判断材质是否为 CAD WebGPU node 线材质。 */
export function isCadNodeLineMaterial(value: unknown): value is CadNodeLineMaterial {
  return (
    typeof value === 'object' &&
    value !== null &&
    'isLine2NodeMaterial' in value &&
    (value as { isLine2NodeMaterial?: boolean }).isLine2NodeMaterial === true
  )
}

/**
 * 同步创建 CAD 宽线对象。
 *
 * WebGL2 复用调用方传入的 `Line2` 构造；WebGPU 必须使用预加载注册的 WebGPU `Line2`，
 * 不能把 `Line2NodeMaterial` 挂到 WebGL `Line2` 上（类实现与 raycast 分辨率语义不同）。
 */
export function createCadLineObjectSync(input: {
  backend: GraphicsBackendKind
  geometry: BufferGeometry
  material: LineMaterial
  createWebGlObject: () => Line2
}): Line2 {
  if (!prefersCadNodeLineMaterial(input.backend)) {
    return input.createWebGlObject()
  }

  if (!registeredWebGpuLineModule) {
    throw new Error('CAD WebGPU 线对象需要先 preload 注册 Line2（CadPlugin.preload）')
  }

  return new registeredWebGpuLineModule.Line2(input.geometry, input.material as CadNodeLineMaterial)
}

/**
 * 按当前 backend 创建 CAD `Line2`。
 *
 * WebGL2 使用 addons 的 `Line2`，WebGPU 使用 `lines/webgpu/Line2`，供实体、工具与标注统一接入。
 */
export function createCadLine2Object(
  geometry: LineGeometry,
  material: LineMaterial,
  backend: GraphicsBackendKind = getCadLineBackend()
): Line2 {
  return createCadLineObjectSync({
    backend,
    geometry,
    material,
    createWebGlObject: () => new Line2(geometry, material),
  })
}

/** 同步创建 CAD `LineSegments2`，WebGPU 分支复用已注册的 WebGPU 宽线模块。 */
export function createCadLineSegments2ObjectSync(input: {
  backend: GraphicsBackendKind
  geometry: BufferGeometry
  material: LineMaterial
  createWebGlObject: () => LineSegments2
}): LineSegments2 {
  if (!prefersCadNodeLineMaterial(input.backend)) {
    return input.createWebGlObject()
  }

  if (!registeredWebGpuLineModule) {
    throw new Error('CAD WebGPU 线对象需要先 preload 注册 LineSegments2（CadPlugin.preload）')
  }

  return new registeredWebGpuLineModule.LineSegments2(
    input.geometry,
    input.material as CadNodeLineMaterial
  )
}

/** 按当前 backend 创建 CAD `LineSegments2`。 */
export function createCadLineSegments2Object(
  geometry: LineSegmentsGeometry,
  material: LineMaterial,
  backend: GraphicsBackendKind = getCadLineBackend()
): LineSegments2 {
  return createCadLineSegments2ObjectSync({
    backend,
    geometry,
    material,
    createWebGlObject: () => new LineSegments2(geometry, material),
  })
}

/** 同时识别 WebGL2 与 WebGPU 版 CAD `Line2`。 */
export function isCadLine2Object(value: unknown): value is Line2 {
  return (
    typeof value === 'object' && value !== null && (value as { isLine2?: boolean }).isLine2 === true
  )
}

/**
 * 判断当前 backend 是否应使用 node 宽线材质。
 *
 * @param backend - 当前图形后端
 * @returns WebGPU 返回 true
 */
export function prefersCadNodeLineMaterial(backend: GraphicsBackendKind): boolean {
  return backend === 'webgpu'
}

/**
 * 把 CAD 线材质参数映射为 `Line2NodeMaterial` 参数。
 *
 * 语义对齐要点：
 * - `resolution` 在 node 材质里由 viewport 推导，必须丢弃（否则参数含义不同）；
 * - `dashScale` → `scale`（node 材质用于 lineDistance 的缩放）；
 * - 颜色/线宽/dash 尺寸/gap 尺寸/depth 语义直接透传。
 */
export function toCadNodeMaterialParameters(
  options: CadLineMaterialParameters
): Record<string, unknown> {
  const {
    resolution: _resolution,
    dashScale,
    ...rest
  } = options as CadLineMaterialParameters & {
    resolution?: Vector2
    dashScale?: number
  }
  const parameters: Record<string, unknown> = { ...rest }
  if (dashScale !== undefined) {
    parameters.scale = dashScale
  }
  return parameters
}

/**
 * 按 backend 创建 CAD 线材质。
 *
 * WebGL2 必须由调用方传入现有工厂（`MaterialUtils.createMaterialByLineType`），保证点划线/
 * 双划线的 GLSL 子类与注册/同步逻辑完全不变；WebGPU 走 `Line2NodeMaterial`。
 *
 * @param input - backend、线型与材质参数
 * @returns 与 backend 匹配的线材质
 */
export async function createCadLineMaterial(input: {
  backend: GraphicsBackendKind
  options: CadLineMaterialParameters
  createWebGlMaterial: (options: CadLineMaterialParameters) => LineMaterial
  pattern?: CadLinePattern
}): Promise<LineMaterial> {
  if (!prefersCadNodeLineMaterial(input.backend)) {
    return input.createWebGlMaterial(input.options)
  }

  const module = await loadCadWebGpuLineModule()
  const material = new module.Line2NodeMaterial(toCadNodeMaterialParameters(input.options))
  applyCadNodeLinePattern(material, input.pattern, module.tsl)
  return material
}

/**
 * 写入当前 View 的屏幕分辨率。
 *
 * WebGL `LineMaterial` 需要显式 resolution；WebGPU node 材质从 viewport 推导，是 no-op。
 */
export function applyCadLineViewportResolution(
  material: LineMaterial,
  width: number,
  height: number
): void {
  const resolution = (material as { resolution?: Vector2 }).resolution
  if (resolution && typeof resolution.set === 'function') {
    resolution.set(width, height)
  }
}

/**
 * 写入虚线缩放：把世界单位距离换算成屏幕像素。
 *
 * WebGL 用 `dashScale`，WebGPU node 材质用 `scale`；两者在各自 shader 中语义等价。
 */
export function applyCadLineDashScale(material: LineMaterial, pixelsPerWorld: number): void {
  if (!Number.isFinite(pixelsPerWorld) || pixelsPerWorld <= 0) {
    return
  }

  const dashScale = (material as { dashScale?: number }).dashScale
  if (typeof dashScale === 'number') {
    ;(material as { dashScale: number }).dashScale = pixelsPerWorld
    return
  }

  const scaleHost = material as unknown as { scale?: number }
  if (typeof scaleHost.scale === 'number') {
    scaleHost.scale = pixelsPerWorld
  }
}

/** node 宽线材质在 WebGPU 下不需要纹理，但保留该类型入口便于调用方统一处理。 */
export type CadLineTexture = Texture | null
